import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class FileDetails extends BaseObj {
    public Base64Data: string | null;
    public __base64data: string | null;

    public Extension: string | null;
    public __extension: string | null;

    public Filename: string | null;
    public __filename: string | null;

    public MaxHeight: number;
    public __maxheight: number;

    public MimeType: string | null;
    public __mimetype: string | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        Base64Data?: string | null;
        EntityState?: EntityState;
        Extension?: string | null;
        Filename?: string | null;
        MaxHeight?: number;
        MimeType?: string | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<FileDetails>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('FileDetails tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.Base64Data = values.Base64Data ?? null;
        this.__base64data = values.Base64Data ?? null;
        Object.defineProperty(this, 'Base64Data', {
            get(): string | null {
                return this.__base64data;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Base64Data', this.__base64data, value);
                this.__base64data = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Extension = values.Extension ?? null;
        this.__extension = values.Extension ?? null;
        Object.defineProperty(this, 'Extension', {
            get(): string | null {
                return this.__extension;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Extension', this.__extension, value);
                this.__extension = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Filename = values.Filename ?? null;
        this.__filename = values.Filename ?? null;
        Object.defineProperty(this, 'Filename', {
            get(): string | null {
                return this.__filename;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('Filename', this.__filename, value);
                this.__filename = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.MaxHeight = values.MaxHeight ?? 0;
        this.__maxheight = values.MaxHeight ?? 0;
        Object.defineProperty(this, 'MaxHeight', {
            get(): number {
                return this.__maxheight;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('MaxHeight', this.__maxheight, value);
                this.__maxheight = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.MimeType = values.MimeType ?? null;
        this.__mimetype = values.MimeType ?? null;
        Object.defineProperty(this, 'MimeType', {
            get(): string | null {
                return this.__mimetype;
            },
            set(value: string | null) {
                this.__entityStateHandler.propertyChanged('MimeType', this.__mimetype, value);
                this.__mimetype = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"Base64Data": ${JSON.stringify(this.Base64Data)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"Extension": ${JSON.stringify(this.Extension)}` +
            ',' +
            `"Filename": ${JSON.stringify(this.Filename)}` +
            ',' +
            `"MaxHeight": ${JSON.stringify(this.MaxHeight)}` +
            ',' +
            `"MimeType": ${JSON.stringify(this.MimeType)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
                     * Sets the entity state to Added
                     *
                     
                     * 
                     * Note: This does not do anything with the children, so use with caution
                     * 
                     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
                     */
    public SetEntityStateAsAddedAndResetId() {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';

        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of FileDetails from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        Base64Data?: string | null;
        Extension?: string | null;
        Filename?: string | null;
        MaxHeight?: number;
        MimeType?: string | null;
        EntityState?: 'Added' | 'Unchanged';
    }): FileDetails {
        return new FileDetails(values);
    }

    public static _FromServerJson(json: any): FileDetails {
        return new FileDetails({
            Base64Data: json.Base64Data ?? '',
            Extension: json.Extension ?? '',
            Filename: json.Filename ?? '',
            MaxHeight: json.MaxHeight,
            MimeType: json.MimeType ?? '',
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of FileDetails from another instance of FileDetails.
     * @param other The FileDetails you want to clone
     * @returns A cloned FileDetails
     */
    public static FromOther(other: FileDetails): FileDetails {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return FileDetails._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return FileDetails.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(...props: ('Base64Data' | 'Extension' | 'Filename' | 'MaxHeight' | 'MimeType')[]) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'FileDetails';
}
